import { useCallback, useState } from "react"

import { Alert, App, Tooltip, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../../../../Components/Button"
import Checkbox from "../../../../../../Components/Checkbox"
import { RoutesPaths } from "../../../../../../constants/user-routes"
import Icon from "../../../../../../Icon"
import Images from "../../../../../../Images"
import { paymentProcessorTitle } from "../../../../../../Utils/utils"
import {
  selectActivatedProcessors,
  selectActiveBuildingBlocks,
  selectActiveProcessor,
  selectBuildingBlocks,
  selectConnectedProcessors,
  selectIsActivated,
  setActiveProcessor
} from "../../redux/configure.slice"
import { activateRecover } from "../../redux/configure.thunks"
import { getFormStepKeys, getSectionLabel, hasPreviewStep } from "../../schema/sections"
import { processorStatuses } from "../../utils/sectionProgress"
import { buildConfigureReadout } from "../../utils/sectionReadout"
import { firstIncompleteStep, isStepComplete } from "../../utils/sectionValidation"

import configureStyles from "../../configure.module.less"
import styles from "./configureSummary.module.less"

const { Title, Text } = Typography

// Readable processor name (same helper Footer/SectionForm keep locally).
const procTitle = (slug) =>
  paymentProcessorTitle?.[slug] || (slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : slug)

// The activate-Recover call to action shown at the top of the finished-setup summary.
// Activation is deliberate (not automatic on finish), so the merchant reviews their
// data and preferences first, then turns Recover on. Two designs, switchable in the
// activation flow (fixed to v3):
//   v1: the summary itself carries the banner (a checklist reminder + a confirm
//       checkbox that gates the Activate button).
//   v2: the summary hands off to Preferences, where the Activate button lives.
const ActivatePanel = ({
  isActivated,
  procCovered,
  multiProcessor,
  procName,
  procSlug,
  missingStep,
  activate,
  navigate,
  t
}) => {
  const { modal } = App.useApp()
  const [attested, setAttested] = useState(false)
  // Activation is impossible while any required field is still empty. `missingStep` is
  // the first step that fails (null when everything passes); the readout below already
  // highlights the individual fields.
  const blocked = !!missingStep
  const blockedReason = blocked
    ? t('Complete "_step_" before activating.', { step: t(getSectionLabel(missingStep)) })
    : ""

  // Activation is tracked PER INTEGRATION (activatedProcessors util): activating with
  // the first processor covers only it, and a processor finished later gets its own
  // activation right here on its own summary.
  if (procCovered) {
    return (
      <Alert
        type="success"
        showIcon
        icon={<Icon type="circle_check_green" style={{ fontSize: 20 }} />}
        className={styles.summaryActiveNote}
        message={
          <Text strong>
            {multiProcessor
              ? t("Recover is active for _name_.", { name: procName })
              : t("Recover is active.")}
          </Text>
        }
        description={
          <Text>
            {t(
              "We're fighting your disputes automatically. You can edit any step below or adjust your Preferences any time."
            )}
          </Text>
        }
      />
    )
  }

  const goToPreferences = () => navigate(RoutesPaths.recover.preferences)
  // Activation is irreversible-feeling (it turns on automatic dispute fighting and the
  // 25% success fee), so gate the button behind an explicit confirm as well as the
  // attestation checkbox above.
  const handleActivate = () => {
    modal.confirm({
      centered: true,
      title: t("Activate Recover?"),
      content: t(
        "Recover will start fighting your disputes automatically, using the profile and Preferences you set up. You can deactivate any time."
      ),
      okText: t("Activate Recover"),
      cancelText: t("Cancel"),
      onOk: () => {
        // The summary is one integration's, so activation covers that one — the other
        // keeps its own wizard and its own activation later.
        activate(procSlug)
        navigate(RoutesPaths.recover.all)
      }
    })
  }

  // Recover is already live (activated with an earlier integration), and THIS
  // integration just finished its setup: activate it individually, no attestation
  // needed again (Preferences are already reviewed and in force).
  if (isActivated) {
    return (
      <div className={styles.summaryActivate}>
        <Title level={5} className={styles.summaryActivateTitle}>
          {t("Activate Recover for _name_", { name: procName })}
        </Title>
        <Text className={styles.summaryActivateLead}>
          {t("Recover is already active. Activate _name_ too so its disputes are covered.", {
            name: procName
          })}
        </Text>
        <Tooltip title={blockedReason}>
          <span>
            <Button
              type="primary"
              size="large"
              disabled={blocked}
              onClick={handleActivate}
              text={t("Activate Recover")}
            />
          </span>
        </Tooltip>
      </div>
    )
  }
  return (
    <div className={styles.summaryActivate}>
      <Title level={5} className={styles.summaryActivateTitle}>
        {blocked ? t("Almost ready to activate Recover") : t("You're ready to activate Recover")}
      </Title>
      <Text className={styles.summaryActivateLead}>
        {blocked
          ? t("Fill in the required fields highlighted below, then activate.")
          : t("Review the details below and edit anything that needs a change.")}
      </Text>
      <Text className={styles.summaryActivateLead}>
        {t("Set up your Preferences so disputes are handled the way you want.")}{" "}
        <Typography.Link onClick={goToPreferences} strong>
          {t("Go to Preferences")}
        </Typography.Link>
      </Text>
      <Checkbox type="bounce" checked={attested} onChange={(e) => setAttested(e.target.checked)}>
        <span className={styles.summaryActivateConfirm}>
          {t("I've reviewed my data and set up my Preferences.")}
        </span>
      </Checkbox>
      <Tooltip title={blockedReason}>
        <span>
          <Button
            type="primary"
            size="large"
            disabled={!attested || blocked}
            onClick={handleActivate}
            text={t("Activate Recover")}
          />
        </span>
      </Tooltip>
    </div>
  )
}

// One-page read-out of every answered field (flow v3): each section's real answers
// rendered inline as label/value rows, no cards to open. An Edit link per section
// jumps back into that step. Empty REQUIRED fields stay in the list as highlighted
// reminders (warning icon + note) so the merchant sees what still needs filling in.
// Render a read-out value, turning any http(s) URL the merchant entered (policy links,
// website, etc.) into a clickable link while leaving surrounding text as-is. Opens in a
// new tab so the summary is not navigated away from.
const renderReadoutValue = (value) => {
  if (typeof value !== "string" || !/https?:\/\//.test(value)) return value
  return value.split(/(https?:\/\/[^\s,]+)/g).map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <Typography.Link
        key={i}
        className={styles.readoutLink}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
      >
        {part}
      </Typography.Link>
    ) : (
      part
    )
  )
}

const FullReadout = ({ buildingBlocks, onEdit, t }) => {
  const sections = buildConfigureReadout(buildingBlocks, t)
  if (!sections.length) return null
  const missingCount = sections.reduce(
    (n, sec) => n + sec.items.filter((it) => it.missing).length,
    0
  )
  return (
    <div className={styles.readout}>
      {missingCount > 0 ? (
        <Alert
          type="warning"
          showIcon
          message={
            <Text strong>
              {missingCount === 1
                ? t("1 required field still needs your attention")
                : t("_n_ required fields still need your attention", { n: missingCount })}
            </Text>
          }
          description={
            <Text>{t("They are highlighted below. Use Edit on a section to fill them in.")}</Text>
          }
        />
      ) : null}
      {sections.map((sec) => (
        <div key={sec.key} className={styles.readoutSection}>
          <div className={styles.readoutHead}>
            <span className={styles.readoutTitle}>{sec.label}</span>
            <Typography.Link className={styles.readoutEdit} onClick={() => onEdit(sec.key)} strong>
              {t("Edit")}
            </Typography.Link>
          </div>
          <dl className={styles.readoutList}>
            {sec.items.map((it, i) => (
              <div key={i} className={styles.readoutRow}>
                <dt className={styles.readoutLabel}>{it.label}</dt>
                {it.missing ? (
                  <dd className={`${styles.readoutValue} ${styles.readoutValueMissing}`}>
                    <Icon type="alert_triangle_error" style={{ fontSize: 16 }} />
                    {t("Required, not filled in yet")}
                  </dd>
                ) : (
                  <dd className={styles.readoutValue}>{renderReadoutValue(it.value)}</dd>
                )}
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  )
}

// Read-only overview shown in the main pane while the finished profile is collapsed
// (the merchant hasn't opened the profile dropdown yet). Mirrors the step-page shell
// (recover-section-form + title); the body is a card grid of the steps. Steps
// with optional questions left are clickable and jump in; completed ones are static.
// The activate call to action sits at the top (see ActivatePanel).
const ConfigureSummary = () => {
  const dispatch = useDispatch()
  const buildingBlocks = useSelector(selectActiveBuildingBlocks)
  const fullBuildingBlocks = useSelector(selectBuildingBlocks)
  const isActivated = useSelector(selectIsActivated)
  const activeProcessor = useSelector(selectActiveProcessor)
  const connectedProcessors = useSelector(selectConnectedProcessors)
  const activatedProcessors = useSelector(selectActivatedProcessors)
  // ActivatePanel takes this as a prop, so keep it stable across renders.
  const activate = useCallback((processor) => dispatch(activateRecover({ processor })), [dispatch])
  const { t } = useTranslation()
  const navigate = useNavigate()
  // Only FORM steps count here: the Preview & Confirm step itself carries no fields.
  const stepKeys = getFormStepKeys()
  // The first step still missing a required field (null when everything passes): gates
  // activation below and explains why. Only the Preview & Confirm flow enforces it.
  const missingStep = hasPreviewStep() ? firstIncompleteStep(buildingBlocks) : null
  // Completion uses the SAME required-only predicate as the rail's green checks
  // (isStepComplete), so the "_done_ of _total_ steps complete" lead agrees with the
  // rail instead of the old answered===total, which counted optional fields too.
  const isDone = (k) => isStepComplete(k, buildingBlocks)
  const doneCount = stepKeys.filter(isDone).length

  // The summary is INDIVIDUAL per integration: buildingBlocks above are the ACTIVE
  // processor's answers, and the switcher below flips between processors. Switching
  // to a not-yet-finished processor drops into its live wizard (ConfigurePage guard).
  const connected = Array.isArray(connectedProcessors) ? connectedProcessors : []
  const multiProcessor = connected.length > 1
  const activeSlug = activeProcessor || connected[0]
  // Which integrations activation covers is the account's answer, so this is simply a
  // membership test — no inference from an empty record, and the same result on any device.
  const procCovered = activatedProcessors.includes(activeSlug)

  // Edit a step: jump straight to its wizard route (the rail + footer come back there).
  const openStep = (k) => navigate(RoutesPaths.recover.configure_section(k))

  // Same switcher markup as the wizard steps (SectionForm), so the summary keeps the
  // exact per-processor navigation the merchant already knows.
  const procStatuses = multiProcessor ? processorStatuses(fullBuildingBlocks, connected) : []
  const procSwitcher = multiProcessor ? (
    <div className={configureStyles.procSwitch}>
      <Text className={configureStyles.procListLabel}>{t("Switch processor")}</Text>
      <div className={configureStyles.procSwitchRow}>
        {procStatuses.map(({ slug, done, total, complete }) => {
          const active = slug === activeSlug
          const logo = Images[`integration_${slug}`]
          return (
            <button
              key={slug}
              type="button"
              aria-pressed={active}
              className={`${configureStyles.procItem} ${active ? configureStyles.active : ""}`}
              onClick={() => {
                if (slug !== activeSlug) dispatch(setActiveProcessor(slug))
              }}
            >
              <span className={configureStyles.procItemLogo}>
                {logo ? (
                  <img src={logo} alt={procTitle(slug)} width={24} height={24} />
                ) : (
                  <Icon type="card" style={{ fontSize: 16 }} />
                )}
              </span>
              <span className={configureStyles.procItemName}>{procTitle(slug)}</span>
              <span className={configureStyles.procItemStatus}>
                {complete ? (
                  <>
                    <Icon type="circle_check_green" style={{ fontSize: 16 }} /> {t("Done")}
                  </>
                ) : (
                  t("_done_ of _total_", { done, total })
                )}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  ) : null

  return (
    <div className="recover-section-form">
      <Title level={4} className={configureStyles.sectionFormTitle}>
        {multiProcessor ? t("Summary for _name_", { name: procTitle(activeSlug) }) : t("Summary")}
      </Title>
      <p className={styles.summaryLead}>
        {t("_done_ of _total_ steps complete", { done: doneCount, total: stepKeys.length })}
      </p>
      {procSwitcher}
      <ActivatePanel
        isActivated={isActivated}
        procCovered={procCovered}
        multiProcessor={multiProcessor}
        procName={procTitle(activeSlug)}
        procSlug={activeSlug}
        missingStep={missingStep}
        activate={activate}
        navigate={navigate}
        t={t}
      />
      <FullReadout buildingBlocks={buildingBlocks} onEdit={openStep} t={t} />
    </div>
  )
}

export default ConfigureSummary
